import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Box, Heading, Alert, AlertIcon, Flex, Spacer, Text, RadioGroup, Radio, Stack, Button } from '@chakra-ui/react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import Select from 'react-select';
import * as XLSX from 'xlsx';

const ViewQuestionsToPaperSet = () => {
  const [paperSets, setPaperSets] = useState([]);
  const [selectedPaperSetId, setSelectedPaperSetId] = useState(null);
  const [selectedPaperSetTitle, setSelectedPaperSetTitle] = useState('');
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterIsActive, setFilterIsActive] = useState('all');

  const gridStyle = useMemo(() => ({ width: '100%', height: '400px' }), []);

  useEffect(() => {
    const fetchPaperSets = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/PaperSet`);
        setPaperSets(Array.isArray(response.data) ? response.data : response.data.$values || []);
      } catch (error) {
        setError('Error fetching paper sets');
        console.error('Error fetching paper sets:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPaperSets();
  }, []);

  useEffect(() => {
    if (selectedPaperSetId) {
      const fetchQuestions = async () => {
        setLoading(true);
        try {
          const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/PaperSet/${selectedPaperSetId}/questions`);
          setQuestions(Array.isArray(response.data.$values) ? response.data.$values : response.data);
        } catch (error) {
          setError('Error fetching questions');
          console.error('Error fetching questions:', error);
        } finally {
          setLoading(false);
        }
      };

      fetchQuestions();
    }
  }, [selectedPaperSetId]);

  const handlePaperSetChange = (selectedOption) => {
    setSelectedPaperSetId(selectedOption?.value || null);
    setSelectedPaperSetTitle(selectedOption?.label || '');
  };

  const filteredPaperSets = useMemo(() => {
    return paperSets.filter((ps) => {
      if (filterIsActive === 'true') return ps.isActive;
      if (filterIsActive === 'false') return !ps.isActive;
      return true;
    });
  }, [paperSets, filterIsActive]);

  const columnDefs = useMemo(() => [
    { headerName: 'Number', valueGetter: 'node.rowIndex + 1', width: 90 },
    { headerName: 'Question', field: 'questionText', flex: 1 },
    {
      headerName: 'Options',
      field: 'options',
      valueGetter: (params) => {
        const options = params.data.options.$values || params.data.options || [];
        return options.map(option => option.optionText).join(', ');
      },
      flex: 2,
    },
    {
      headerName: 'Correct Answer',
      field: 'correctAnswer',
      valueGetter: (params) => {
        const options = params.data.options.$values || params.data.options || [];
        const correctOption = options.find((option) => option.isCorrect);
        return correctOption ? correctOption.optionText : 'N/A';
      },
      flex: 1,
    },
  ], []);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
  }), []);

  const rowData = useMemo(() => questions.map((question) => ({
    questionId: question.questionId,
    questionText: question.questionText,
    options: question.options?.$values || question.options || [],
  })), [questions]);

  const paperSetOptions = filteredPaperSets.map(paperSet => ({
    value: paperSet.paperSetId,
    label: paperSet.title
  }));

  const exportToExcel = () => {
    // Prepare the data for export
    const formattedData = rowData.map((row, index) => {
      const options = row.options || [];
      return {
        Number: index + 1,
        Question: row.questionText,
        Option1: options[0]?.optionText || '',
        Option2: options[1]?.optionText || '',
        Option3: options[2]?.optionText || '',
        Option4: options[3]?.optionText || '',
        CorrectAnswer: options.find(opt => opt.isCorrect)?.optionText || 'N/A',
      };
    });
  
    // Create a worksheet
    const worksheet = XLSX.utils.aoa_to_sheet([
      [`Paper Set: ${selectedPaperSetTitle}`], // Paper set title row
      ['Number', 'Question', 'Option1', 'Option2', 'Option3', 'Option4', 'CorrectAnswer'], // Column headers
    ]);
  
    // Add the data starting from the third row
    XLSX.utils.sheet_add_json(worksheet, formattedData, { origin: 'A3', skipHeader: true });
  
    // Merge the title row across all columns
    const columnCount = 7; // Adjust based on the number of columns
    worksheet['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: columnCount - 1 } }, // Merge cells A1 to G1
    ];
  
    // Initialize the workbook
    const workbook = XLSX.utils.book_new();
  
    // Sanitize the sheet name to remove invalid characters
    const sanitizedSheetName = (selectedPaperSetTitle || 'Export')
      .replace(/[:\\/?*[\]]/g, '_') // Replace invalid characters with '_'
      .slice(0, 31); // Ensure the name is within Excel's 31-character limit
  
    // Append the worksheet to the workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, sanitizedSheetName);
  
    // Write the workbook to a file
    XLSX.writeFile(workbook, `${sanitizedSheetName}.xlsx`);
  };
  
  

  return (
    <Box className="relative lg:w-[80%] md:w-3/4 w-full mx-auto mt-10">
      <div className="absolute inset-0 bg-gradient-to-r from-blue-300 to-blue-600 shadow-lg transform -skew-y-12 sm:skew-y-0 sm:-rotate-[8deg] rounded-3xl drop-shadow-md"></div>

      <Box className="relative bg-white rounded-2xl drop-shadow-md p-8 flex flex-col mt-10 space-y-6 z-10">
        <Flex align="center" className="gap-4 border-b pb-3 border-gray-300">
          <h1 className="text-2xl font-bold mb-6">View Questions in Paper Set</h1>
          <Spacer />
          <Heading size="md" className="mb-6">{selectedPaperSetTitle}</Heading>
        </Flex>

        {error && (
          <Alert status="error" mb={4}>
            <AlertIcon />
            {error}
          </Alert>
        )}

        <RadioGroup value={filterIsActive} onChange={setFilterIsActive} mb={4}>
          <Stack direction="row">
            <Radio value="all">All</Radio>
            <Radio value="true">Active</Radio>
            <Radio value="false">Inactive</Radio>
          </Stack>
        </RadioGroup>

        <Select
          options={paperSetOptions}
          placeholder="Select Paper Set"
          onChange={handlePaperSetChange}
          className="mb-6"
          isClearable
          isSearchable
        />

        <Flex justifyContent="space-between" alignItems="center" mb={4}>
          <Text>Total Questions: {questions.length}</Text>
          <Button colorScheme="blue" onClick={exportToExcel}>Export to Excel</Button>
        </Flex>

        <div className="ag-theme-alpine" style={gridStyle}>
          <AgGridReact
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            pagination={true}
            paginationPageSize={10}
          />
        </div>
      </Box>
    </Box>
  );
};

export default ViewQuestionsToPaperSet;
